import { MapPin, TrendingUp, Info } from "lucide-react";
import { Seal } from "./Seal";

// Raw-data breakdown config for each pillar. Financial shows a real, publicly
// reported figure (endowment); the others show clearly-labelled, model-derived
// indices (underlying absolute counts are not part of the source dataset).
const CATEGORIES = [
  {
    key: "financial",
    label: "Financial Resources",
    weight: 35,
    statLabel: "Endowment",
    real: true,
    info: "Latest publicly reported endowment (approximate). Verify against the institution's most recent financial statement before publication.",
  },
  {
    key: "impact",
    label: "Impact & Innovation",
    weight: 30,
    statLabel: "Innovation Index",
    info: "Model-derived index of research output (patents, sponsored grants and publications). Underlying absolute counts are not part of the current dataset.",
  },
  {
    key: "prestige",
    label: "Prestige & Value",
    weight: 20,
    statLabel: "Prestige Index",
    info: "Model-derived index of selectivity, graduate outcomes and peer reputation.",
  },
  {
    key: "sustainability",
    label: "Sustainability",
    weight: 10,
    statLabel: "Adaptability Index",
    info: "A composite measure of the institution\u2019s social, environmental, and operational flexibility based on current reporting.",
  },
  {
    key: "brand",
    label: "Brand Awareness",
    weight: 5,
    statLabel: "Media Visibility Index",
    info: "Model-derived index of news mentions and search interest. Underlying absolute volumes are not part of the current dataset.",
  },
];

function formatUSD(n) {
  if (n == null) return null;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${Math.round(n / 1e6)}M`;
  return `$${Math.round(n).toLocaleString()}`;
}

function InfoDot({ text, testid }) {
  return (
    <span className="dash-info" tabIndex={0} data-testid={testid}>
      <Info size={12} strokeWidth={2.2} />
      <span className="dash-info-pop" role="tooltip">
        {text}
      </span>
    </span>
  );
}

function Bar({ value, color }) {
  const v = value == null ? 0 : value;
  return (
    <div className="dash-bar-track">
      <div className="dash-bar-fill" style={{ width: `${v}%`, background: color }} />
    </div>
  );
}

export function ResiliencyDashboard({ institution }) {
  if (!institution) {
    return (
      <section className="hud-dashboard hud-dashboard-empty" data-testid="dashboard-panel">
        <p className="dash-empty">
          Hover a node or select an institution to view its Resiliency Dashboard.
        </p>
      </section>
    );
  }

  const color = institution.color || "#B8912F";

  const rawStatValue = (c) => {
    if (c.real) {
      return formatUSD(institution.endowment_usd) ?? "Not reported";
    }
    const v = institution[c.key];
    return v == null ? "N/A" : `${v.toFixed(1)} / 100`;
  };

  return (
    <section
      className="hud-dashboard"
      data-testid="dashboard-panel"
      style={{ borderTop: `3px solid ${color}` }}
    >
      <div className="dash-head">
        <div className="dash-head-row">
          <Seal inst={institution} size={54} testid="dashboard-seal" />
          <div className="dash-head-text">
            <span className="hud-eyebrow" style={{ color }}>
              Resiliency Dashboard
            </span>
            <h2
              className="dash-name"
              data-testid="dashboard-institution-name"
              style={{ color }}
            >
              {institution.name}
            </h2>
          </div>
        </div>
        <p className="dash-meta">
          <MapPin size={13} strokeWidth={1.8} />
          {institution.city}, {institution.state}
          <span className="dash-dot">·</span>
          {institution.footprint_acres.toLocaleString()} acres
        </p>
      </div>

      <div className="dash-score-row">
        <div className="dash-score-big" data-testid="dashboard-score">
          {institution.resiliency_rating.toFixed(2)}
        </div>
        <div className="dash-score-label" style={{ color }}>
          <TrendingUp size={14} strokeWidth={1.8} />
          Composite Rating · Rank #{institution.rank}
        </div>
      </div>

      <div className="dash-categories">
        {CATEGORIES.map((c) => {
          const raw = institution[c.key];
          return (
            <div className="dash-cat" key={c.key} data-testid={`dashboard-cat-${c.key}`}>
              <div className="dash-cat-top">
                <span className="dash-cat-label">
                  {c.label}
                  <em className="dash-cat-weight">{c.weight}%</em>
                </span>
                <span className="dash-cat-value">
                  {raw == null ? "N/A" : raw.toFixed(1)}
                </span>
              </div>
              <Bar value={raw} color={color} />
              <div className="dash-rawstat" data-testid={`dashboard-rawstat-${c.key}`}>
                <span className="dash-rawstat-label">
                  {c.statLabel}
                  <InfoDot text={c.info} testid={`dashboard-rawinfo-${c.key}`} />
                </span>
                <span
                  className="dash-rawstat-value"
                  data-testid={`dashboard-rawvalue-${c.key}`}
                >
                  {rawStatValue(c)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <p className="dash-source-note" data-testid="dashboard-source-note">
        Endowment figures are latest publicly reported (approx.). Index values are
        model-derived from the TBTF Resiliency Model.
      </p>
    </section>
  );
}
